import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Pencil } from "@ews-admin/global-design-system";
import { doctorLicencePricingApi } from "../../services/doctorLicencePricing.api";
import { featureApi } from "../../services/feature.api";
import { DoctorLicencePricing } from "../../types/doctorLicencePricing.definition";
import { DoctorLicencePlanType } from "../../types/enums";
import { FeatureOption } from "../../types/feature.definition";
import { CURRENCY_SUFFIX } from "../../constants";
import { formatMoney } from "../../utils/money-utils";
import { getFeatureLabel } from "../../utils/feature-label-utils";
import {
  PlanEditorPanel,
  PlanEditorValues,
} from "../../components/molecules/PlanEditorPanel/PlanEditorPanel";
import { stringToFeatures } from "../../components/atoms/FeatureListEditor";
import { ToggleSwitch } from "../../components/atoms/ToggleSwitch";
import { DoctorLicenceFeatureInfo } from "../../types/doctorLicencePricing.definition";

const featureInfoToString = (
  features: DoctorLicenceFeatureInfo[] | null | undefined
): string | null =>
  features && features.length > 0 ? features.map(f => f.code).join("\n") : null;

const PLAN_META: Record<
  DoctorLicencePlanType,
  { accentBg: string; badge: string; label: string }
> = {
  [DoctorLicencePlanType.BASIC]: {
    accentBg: "bg-slate-500",
    badge: "bg-slate-100 text-slate-600",
    label: "Basic",
  },
  [DoctorLicencePlanType.STANDARD]: {
    accentBg: "bg-primary-600",
    badge: "bg-primary-50 text-primary-700",
    label: "Standard",
  },
  [DoctorLicencePlanType.PREMIUM]: {
    accentBg: "bg-amber-500",
    badge: "bg-amber-50 text-amber-700",
    label: "Premium",
  },
};

const PLAN_FIELDS = (t: (k: string) => string) => [
  {
    key: "basePrice",
    label: t("admin.doctorPricing.table.basePrice"),
    note: t("admin.doctorPricing.basePriceNote"),
    suffix: CURRENCY_SUFFIX,
    min: 0,
    step: 1000,
  },
  {
    key: "pricePerMonth",
    label: t("admin.doctorPricing.table.pricePerMonth"),
    note: t("admin.doctorPricing.pricePerMonthNote"),
    suffix: CURRENCY_SUFFIX,
    min: 0,
    step: 1000,
  },
];

export const DoctorLicencePricingManagement: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [pricings, setPricings] = useState<DoctorLicencePricing[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [editingPlan, setEditingPlan] = useState<DoctorLicencePricing | null>(
    null
  );
  const [saving, setSaving] = useState(false);
  const [availableFeatures, setAvailableFeatures] = useState<FeatureOption[]>(
    []
  );

  useEffect(() => {
    doctorLicencePricingApi
      .getAll()
      .then(setPricings)
      .catch(() => setErrorMsg(t("admin.doctorPricing.errorLoading")))
      .finally(() => setLoading(false));
  }, [t]);

  // MED-1558: feature labels in the UI language, refetched when it changes.
  useEffect(() => {
    featureApi
      .getAll(i18n.language)
      .then(fs =>
        setAvailableFeatures(fs.map(f => ({ code: f.code, label: f.label })))
      );
  }, [i18n.language]);

  const openEditor = (p: DoctorLicencePricing) => {
    setErrorMsg(null);
    setSuccessMsg(null);
    setEditingPlan(p);
  };

  const handleToggleEnabled = async (
    plan: DoctorLicencePricing,
    nextEnabled: boolean
  ) => {
    // Optimistic update
    setPricings(prev =>
      prev.map(p => (p.id === plan.id ? { ...p, enabled: nextEnabled } : p))
    );
    try {
      const updated = await doctorLicencePricingApi.updateEnabled(
        plan.planType,
        nextEnabled
      );
      setPricings(prev => prev.map(p => (p.id === plan.id ? updated : p)));
      const planLabel = (
        PLAN_META[plan.planType] ?? PLAN_META[DoctorLicencePlanType.BASIC]
      ).label;
      toast.success(
        t("admin.doctorLicence.plans.toggleSuccess", {
          plan: planLabel,
          status: nextEnabled
            ? t("admin.doctorLicence.plans.enabled")
            : t("admin.doctorLicence.plans.disabled"),
        })
      );
    } catch {
      // Roll back
      setPricings(prev =>
        prev.map(p => (p.id === plan.id ? { ...p, enabled: plan.enabled } : p))
      );
      toast.error(t("admin.doctorLicence.plans.toggleError"));
    }
  };

  const handleSave = async (values: PlanEditorValues) => {
    if (!editingPlan) return;
    setSaving(true);
    setErrorMsg(null);
    try {
      const updated = await doctorLicencePricingApi.update(editingPlan.id, {
        planType: editingPlan.planType,
        basePrice: Number(values.basePrice),
        pricePerMonth: Number(values.pricePerMonth),
        features: values.features,
      });
      setPricings(prev =>
        prev.map(p => (p.id === editingPlan.id ? updated : p))
      );
      setEditingPlan(null);
      setSuccessMsg(t("admin.doctorPricing.updateSuccess"));
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch {
      setErrorMsg(t("admin.doctorPricing.updateError"));
    } finally {
      setSaving(false);
    }
  };

  const editorValues: PlanEditorValues | null = editingPlan
    ? {
        basePrice: editingPlan.basePrice,
        pricePerMonth: editingPlan.pricePerMonth,
        features: featureInfoToString(editingPlan.features),
      }
    : null;

  return (
    <div>
      {successMsg && (
        <div className="p-3 mb-6 text-green-700 bg-green-50 rounded-lg border border-green-200 text-sm">
          {successMsg}
        </div>
      )}
      {errorMsg && (
        <div className="p-3 mb-6 text-red-700 bg-red-50 rounded-lg border border-red-200 text-sm">
          {errorMsg}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 rounded-full border-b-2 border-primary-600 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {pricings.map(p => {
            const meta =
              PLAN_META[p.planType] ?? PLAN_META[DoctorLicencePlanType.BASIC];
            const features = stringToFeatures(featureInfoToString(p.features));

            return (
              <div
                key={p.id}
                className="rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col"
              >
                <div className={`h-1.5 ${meta.accentBg}`} />

                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                  <div>
                    <span
                      className={`inline-block text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full mb-1 ${meta.badge}`}
                    >
                      {t("admin.doctorPricing.plan")}
                    </span>
                    <h3 className="text-base font-bold text-gray-900">
                      {meta.label}
                    </h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openEditor(p)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                      {t("common.edit")}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-b border-gray-100">
                  <div className="flex flex-col">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {t("admin.doctorLicence.plans.status")}
                    </span>
                    <span
                      className={`text-sm font-semibold ${
                        p.enabled ? "text-success-700" : "text-gray-500"
                      }`}
                    >
                      {p.enabled
                        ? t("admin.doctorLicence.plans.enabled")
                        : t("admin.doctorLicence.plans.disabled")}
                    </span>
                  </div>
                  <ToggleSwitch
                    checked={!!p.enabled}
                    onChange={next => handleToggleEnabled(p, next)}
                    ariaLabel={t("admin.doctorLicence.plans.toggleAriaLabel", {
                      plan: meta.label,
                    })}
                  />
                </div>

                <div className="px-6 py-5 flex-1 space-y-4">
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                      {t("admin.doctorPricing.table.basePrice")}
                    </p>
                    <p className="text-xl font-bold text-gray-900">
                      {formatMoney(p.basePrice)}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {t("admin.doctorPricing.basePriceNote")}
                    </p>
                  </div>

                  <div className="border-t border-gray-100" />

                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                      {t("admin.doctorPricing.table.pricePerMonth")}
                    </p>
                    <p className="text-xl font-bold text-gray-900">
                      {formatMoney(p.pricePerMonth)}
                      <span className="text-sm font-normal text-gray-400 ml-1">
                        / {t("admin.doctorPricing.perMonth")}
                      </span>
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {t("admin.doctorPricing.pricePerMonthNote")}
                    </p>
                  </div>

                  {features.length > 0 && (
                    <>
                      <div className="border-t border-gray-100" />
                      <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                          {t("admin.planEditor.sectionFeatures")}
                        </p>
                        <ul className="space-y-1">
                          {features.map((code, i) => {
                            const featureLabel = getFeatureLabel(
                              code,
                              availableFeatures,
                              t
                            );
                            return (
                              <li
                                key={i}
                                className="flex items-center gap-2 text-sm text-gray-700"
                              >
                                <span
                                  className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${meta.accentBg}`}
                                />
                                {featureLabel}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editingPlan && editorValues && (
        <PlanEditorPanel
          open={!!editingPlan}
          planMeta={
            PLAN_META[editingPlan.planType] ??
            PLAN_META[DoctorLicencePlanType.BASIC]
          }
          planLabel={
            (
              PLAN_META[editingPlan.planType] ??
              PLAN_META[DoctorLicencePlanType.BASIC]
            ).label
          }
          fields={PLAN_FIELDS(t)}
          values={editorValues}
          availableFeatures={availableFeatures}
          saving={saving}
          onSave={handleSave}
          onClose={() => setEditingPlan(null)}
          previewConfig={{
            primaryPriceKey: "pricePerMonth",
            primaryPriceSuffix: `/ ${t("admin.doctorPricing.perMonth")}`,
            secondaryPriceKey: "basePrice",
            secondaryPriceSuffix: t("admin.doctorPricing.basePriceNote"),
          }}
        />
      )}
    </div>
  );
};
